import { useMemo } from "react";
import styles from "./SparkChart.module.scss";
import dayjs from "dayjs";
import { negativeState, positiveState } from "@/lib/store";
import { useAtomValue } from "jotai";
import TimeSeriesChart, { type TimeSeries, type TimeSeriesLine } from "@/components/ortex-charts/TimeSeriesChart";

type Props = {
    data: Array<Array<number>>;
    triggerLevel?: number;
};

/** The last 24 hours of an alert's price series with its trigger level. */
const SparkChart = ({ data, triggerLevel }: Props) => {
    const negativeColor = useAtomValue(negativeState);
    const positiveColor = useAtomValue(positiveState);

    const series = useMemo<TimeSeries[]>(() => {
        const since = dayjs().subtract(24, "hour").valueOf();
        const points = (data ?? []).filter((point) => point[0] >= since).map((p) => [p[0], p[1]] as [number, number]);
        return [{ id: "price", title: "Price", type: "spline", data: points, color: positiveColor, precision: 2, legend: false }];
    }, [data, positiveColor]);
    const lines = useMemo<TimeSeriesLine[] | undefined>(() => (triggerLevel ? [{ value: triggerLevel, color: negativeColor, title: "Trigger Level", lineStyle: "solid" }] : undefined), [triggerLevel, negativeColor]);

    return (
        <div className={styles.SparkChart} style={{ height: "100%", width: "100%" }}>
            {!data?.length ? (
                <p className={styles["no-chart"]}>N/A</p>
            ) : (
                <TimeSeriesChart
                    series={series}
                    lines={lines}
                    resolution="1"
                    timeAxis={false}
                    grid={false}
                    legend={false}
                    interactive={false}
                    axes={[{ id: "right", visible: false }]}
                    tooltipTitle={(t) => dayjs(t).format("dddd, MMM D, YYYY HH:mm")}
                />
            )}
        </div>
    );
};

export default SparkChart;
